if (!customElements.get('pdp-sticky-atc')) {
  customElements.define(
    'pdp-sticky-atc',
    class PdpStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.realSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.button = this.querySelector('[data-pdp-sticky-atc-button]');

        this.setupVisibilityObserver();
        this.setupVariantSync();
        this.setupSubmitMirror();
        this.setupClickForwarding();
      }

      disconnectedCallback() {
        this.discoverObserver?.disconnect();
        this.buyButtonsObserver?.disconnect();
        this.submitMirrorObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
      }

      setupVisibilityObserver() {
        // Primary trigger: tied to the "Discover" (related-products)
        // section being reached, rather than inferred from the buy buttons
        // scrolling away — on shorter PDPs there's barely any gap between
        // "buy buttons gone" and "footer approaching", which left no window
        // for a buy-buttons-based trigger to ever show the card.
        //
        // A plain isIntersecting on that section is too narrow though: on
        // a stacked mobile layout the section's own on-screen slice can be
        // short enough to scroll past entirely within the same stretch the
        // buy-buttons safety check below is still suppressing it, so the
        // card never gets a turn to show at all. The large top rootMargin
        // makes it latch "reached" as soon as the section's bottom comes
        // within 2000px of the viewport and keeps counting it as visible
        // for the rest of the scroll down (until scrolled back above it by
        // that same margin) instead of only its exact on-screen window.
        this.discoverReached = false;
        this.buyButtonsVisible = false;

        const discoverSection = document.querySelector('.related-products');
        if (discoverSection) {
          this.discoverObserver = new IntersectionObserver(
            (entries) => {
              this.discoverReached = entries[0].isIntersecting;
              this.updateVisibility();
            },
            { rootMargin: '2000px 0px 0px 0px' }
          );
          this.discoverObserver.observe(discoverSection);
        }

        // No separate footer check: both reference designs show the card
        // still floating near the very bottom of the page (over Recently
        // Viewed), and every attempt at "hide once the footer is close"
        // ended up firing on the same stretch discoverReached only just
        // turned on for shorter pages, permanently suppressing it — the
        // buy-buttons check below is the only other suppression needed.
        //
        // Safety check: also suppress it whenever the real buy buttons row
        // is still on screen at the same time — on narrower desktop widths
        // (~1024-1280px) the page reflows taller and Discover can be
        // reached while the real Add to Cart button is still visible
        // above it, which put both on screen together.
        const buyButtonsRow = this.realSubmitButton?.closest('.product-form__buttons');
        if (buyButtonsRow) {
          this.buyButtonsObserver = new IntersectionObserver((entries) => {
            this.buyButtonsVisible = entries[0].isIntersecting;
            this.updateVisibility();
          });
          this.buyButtonsObserver.observe(buyButtonsRow);
        }
      }

      updateVisibility() {
        this.hidden = !this.discoverReached || this.buyButtonsVisible;
      }

      setupVariantSync() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;
          this.updateFromVariant(event.data.variant);
        });
      }

      updateFromVariant(variant) {
        const variantLabel = this.querySelector('[data-pdp-sticky-atc-variant]');
        const image = this.querySelector('[data-pdp-sticky-atc-image]');

        if (!variant) return;

        if (variantLabel) {
          // The color half doesn't come from the variant at all (it's a
          // metafield, rendered once server-side into data-color) — only
          // the size half changes per selection, so it's the only part
          // rebuilt here. Re-using variant.title verbatim would silently
          // drop the color again on every selection change.
          const color = this.dataset.color;
          variantLabel.textContent = color && variant.title !== color ? `${color} / ${variant.title}` : variant.title;
        }
        if (image && variant.featured_image?.src) image.src = variant.featured_image.src;
      }

      setupSubmitMirror() {
        if (!this.realSubmitButton || !this.button) return;

        const mirrorState = () => {
          const label = this.realSubmitButton.querySelector('span')?.innerHTML;
          const buttonLabel = this.querySelector('[data-pdp-sticky-atc-button-label]');
          if (label && buttonLabel) buttonLabel.innerHTML = label;

          const isDisabled =
            this.realSubmitButton.hasAttribute('disabled') ||
            this.realSubmitButton.getAttribute('aria-disabled') === 'true';
          this.button.toggleAttribute('disabled', isDisabled);
          this.button.classList.toggle('loading', this.realSubmitButton.classList.contains('loading'));
        };

        mirrorState();
        this.submitMirrorObserver = new MutationObserver(mirrorState);
        this.submitMirrorObserver.observe(this.realSubmitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
        });
      }

      setupClickForwarding() {
        if (!this.button || !this.realSubmitButton) return;

        this.button.addEventListener('click', () => {
          if (this.button.hasAttribute('disabled')) return;
          this.realSubmitButton.click();
        });
      }
    }
  );
}
